import React, { useMemo, useState } from 'react';

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  LinkOutlined,
  SyncOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import VideoPreview from '@shared/VideoPreview';
import { Can } from '@shared/permissions';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { Button, Card, Descriptions, Empty, Flex, Modal, Table, Tabs, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';

import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { returnOrderService } from '@services/operator/returnOrderService';
import returnReceiptService, { type ReturnReceiptDetail } from '@services/operator/returnReceiptService';

import { formatDate, formatDateTime } from '../../../utils/dateTime';

// VideoPreview is .jsx; TS can't infer destructured prop types under strict mode.
// Cast to any — same workaround used by shared/VideoSection.tsx.
const VideoPreviewAny = VideoPreview as any;

type ReceiptItem = ReturnReceiptDetail['items'][number];

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  pending: { color: 'default', icon: <SyncOutlined /> },
  in_progress: { color: 'processing', icon: <ClockCircleOutlined /> },
  verified: { color: 'processing', icon: <CheckCircleOutlined /> },
  done: { color: 'success', icon: <CheckCircleOutlined /> },
  canceled: { color: 'error', icon: <CloseCircleOutlined /> },
};

export default function ReturnReceiptShow() {
  const receipt = useLoaderData() as ReturnReceiptDetail | null;
  const { revalidate } = useRevalidator();
  const { t } = useTranslation();
  const [reopenLoading, setReopenLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Fetch attribute definitions for the receipt's merchant so the
  // custom_attributes column can render localized labels (e.g. "color" → "顏色").
  // Hook MUST stay above the early-return below — Rules of Hooks.
  const attributeDefinitions = useInventoryAttributeDefinitions(
    'operator',
    receipt?.merchant_id,
  );
  const attributeLabelByName = useMemo(
    () =>
      Object.fromEntries(
        attributeDefinitions.map((d) => [d.name, d.label ?? d.name]),
      ),
    [attributeDefinitions],
  );

  // Hook MUST stay above the early-return below — Rules of Hooks.
  const serialNumberColumns = useMemo(() => [
    {
      nameKey: 'models.serial_number.number',
      persist: true,
      width: '150px',
      selector: ({ number }: any) => number,
    },
    {
      nameKey: 'models.serial_number.product_sku',
      width: '150px',
      selector: ({ product_sku }: any) => product_sku || '-',
    },
    {
      nameKey: 'models.serial_number.product_name',
      width: '200px',
      wrap: true,
      selector: ({ product_name }: any) => product_name || '-',
    },
    {
      nameKey: 'common.created_at',
      width: '180px',
      selector: ({ created_at }: any) => created_at,
    },
  ], [t]);

  if (!receipt) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }

  const handleReopen = () => {
    Modal.confirm({
      title: t('shared.return_orders.detail.continue_verify_confirm_title'),
      content: t('shared.return_orders.detail.continue_verify_confirm_content'),
      okText: t('shared.return_orders.detail.continue_verify_confirm_ok'),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setReopenLoading(true);
        try {
          const res = await returnReceiptService.reopen(receipt.id);
          if (res.success) {
            message.success(t('shared.return_orders.detail.continue_verify_success'));
            revalidate();
          } else {
            message.error(
              t(`errors.${res.message}`, {
                ...(res.interpolations || {}),
                defaultValue: t('common.error.operation_failed'),
              })
            );
          }
        } catch (err) {
          console.error('Failed to reopen return receipt:', err);
          message.error(t('common.error.operation_failed'));
        } finally {
          setReopenLoading(false);
        }
      },
    });
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t('shared.return_orders.detail.void_receipt_confirm_title'),
      content: t('shared.return_orders.detail.void_receipt_confirm_content'),
      okText: t('shared.return_orders.detail.void_receipt_confirm_ok'),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setCancelLoading(true);
        try {
          const res = await returnReceiptService.cancel(receipt.id);
          if (res.success) {
            message.success(t('shared.return_orders.detail.void_receipt_success'));
            revalidate();
          } else {
            message.error(
              t(`errors.${res.message}`, {
                ...(res.interpolations || {}),
                defaultValue: t('common.error.operation_failed'),
              })
            );
          }
        } catch (err) {
          console.error('Failed to void return receipt:', err);
          message.error(t('common.error.operation_failed'));
        } finally {
          setCancelLoading(false);
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {t(`enums.return_receipt.status.${status}`, { defaultValue: status })}
      </Tag>
    );
  };

  const itemColumns: ColumnsType<ReceiptItem> = [
    {
      title: t('models.return_order_item.product'),
      key: 'product',
      render: (_, record) => {
        const name = record.product?.name || '-';
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.product_id ? (
                <Link to={`/products/${record.product_id}`}>{name}</Link>
              ) : (
                name
              )}
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.product?.sku || ''}</div>
          </div>
        );
      },
    },
    {
      title: t('models.return_order_item.inventory_type'),
      key: 'inventory_type',
      render: (_, record) => record.inventory_type?.name || '-',
    },
    {
      title: t('models.return_order_item.manufacturing_date', { defaultValue: '製造日期' }),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (v: string | null) => (v ? formatDate(v) : '-'),
    },
    {
      title: t('models.return_order_item.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v: string | null) => (v ? formatDate(v) : '-'),
    },
    {
      title: t('models.return_order_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (v: Record<string, unknown> | null) => (
        <CustomAttributesTags attributes={v} attributeLabelByName={attributeLabelByName} />
      ),
    },
    {
      title: t('models.return_order_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (quantity: number | null, record) => {
        const verified = record.received_quantity ?? 0;
        const total = quantity ?? 0;
        const hasDiscrepancy = verified !== total;
        return (
          <div style={{ textAlign: 'right' }}>
            <div>
              <span style={{
                color: hasDiscrepancy ? '#ff4d4f' : '#000',
                fontWeight: hasDiscrepancy ? 500 : 400,
              }}>{verified}</span>
              <span style={{ color: '#999', margin: '0 4px' }}>/</span>
              <span style={{ color: '#666' }}>{total}</span>
            </div>
            {hasDiscrepancy && (
              <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                {verified > total
                  ? t('shared.return_orders.detail.over_received')
                  : t('shared.return_orders.detail.under_received')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('models.return_order_item.note'),
      dataIndex: 'note',
      key: 'note',
      render: (v: string | null) => v || '-',
    },
  ];

  const breadcrumbItems: Array<string | { label: string; path?: string }> = [
    { label: t('operator.menu.return_orders.title') },
    { label: t('operator.menu.return_orders.list'), path: '/return_orders' },
    ...(receipt.return_order
      ? [{ label: receipt.return_order.name, path: `/return_orders/${receipt.return_order.id}` }]
      : []),
    { label: receipt.name },
  ];

  // Receipt-scoped video filter on top of RO videos endpoint — backend has no
  // /return_receipts/:id/videos, so client-side filters by return_receipt_id.
  const returnOrderId = receipt.return_order_id;
  const videoAdapter = returnOrderId
    ? {
        getVideos: async () => {
          const data = await returnOrderService.getVideos(returnOrderId);
          if (!data?.success) return data;
          const videos = (data.data?.videos || []).filter(
            (v: { return_receipt_id?: string }) => v.return_receipt_id === receipt.id
          );
          return { ...data, data: { ...data.data, videos } };
        },
      }
    : null;

  return (
    <div>
      <ListPageHeader
        basePath="/operator"
        items={breadcrumbItems}
        actions={
          receipt.reopenable || receipt.cancelable ? (
            <Flex gap={8}>
              {receipt.reopenable && (
                <Can requiredPermission="return_orders:write">
                  <Button
                    icon={<UndoOutlined />}
                    loading={reopenLoading}
                    onClick={handleReopen}
                  >
                    {t('shared.return_orders.detail.continue_verify_button')}
                  </Button>
                </Can>
              )}
              {receipt.cancelable && (
                <Can requiredPermission="return_orders:write">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={cancelLoading}
                    onClick={handleCancel}
                  >
                    {t('shared.return_orders.detail.void_receipt_button')}
                  </Button>
                </Can>
              )}
            </Flex>
          ) : null
        }
      />

      <Descriptions column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('models.return_receipt.name')}>
          {receipt.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.return_order.status')}>
          {getStatusTag(receipt.status)}
        </Descriptions.Item>

        {receipt.merchant?.name && (
          <Descriptions.Item label={t('models.return_order.merchant')}>
            {receipt.merchant.name}
          </Descriptions.Item>
        )}

        <Descriptions.Item label={t('models.return_order.name')}>
          {receipt.return_order ? (
            <Flex align="center" justify="space-between" style={{ width: '100%' }}>
              <Link to={`/return_orders/${receipt.return_order.id}`}>
                {receipt.return_order.name}
              </Link>
              <a
                href={`/operator/return_orders/${receipt.return_order.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkOutlined style={{ fontSize: 12, color: '#999' }} />
              </a>
            </Flex>
          ) : '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t('models.return_receipt.tracking_number')}>
          {receipt.tracking_number || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('shared.return_orders.receipts.box_count')}>
          {receipt.box_count ?? '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t('shared.return_orders.receipts.verifier')}>
          {receipt.verifier_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.return_receipt.verified_at')}>
          {receipt.verified_at ? formatDateTime(receipt.verified_at) : '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t('shared.return_orders.receipts.inbound')}>
          {receipt.inbound_id ? (
            <Link to={`/inbounds/${receipt.inbound_id}`}>
              {receipt.inbound_name || receipt.inbound_id}
            </Link>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.return_order.note')}>
          {receipt.note || '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t('common.created_at')}>
          {receipt.created_at ? formatDateTime(receipt.created_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.updated_at')}>
          {receipt.updated_at ? formatDateTime(receipt.updated_at) : '-'}
        </Descriptions.Item>
      </Descriptions>

      {receipt.items && receipt.items.length > 0 && (
        <Card title={t('shared.return_orders.detail.items')} style={{ marginBottom: 16 }}>
          <Table<ReceiptItem>
            columns={itemColumns}
            dataSource={receipt.items}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}

      {videoAdapter && (
        <Card title={t('shared.return_orders.detail.videos')} style={{ marginBottom: 16 }}>
          <VideoPreviewAny orderId={receipt.id} orderService={videoAdapter} />
        </Card>
      )}

      <Tabs
        destroyInactiveTabPane
        items={[{
          key: 'serial_numbers',
          label: t('models.return_receipt.tabs.serial_numbers', { defaultValue: '序號' }),
          children: (
            <TableIndex
              columns={serialNumberColumns}
              service={{
                // Service returns the discriminated success | ApiErrorEnvelope union;
                // TableIndex's getRecords signature only models the success shape, so
                // cast at the boundary — same workaround used by other TableIndex
                // call sites in this repo (e.g. shared/inbounds/InboundShow.tsx).
                getRecords: ((params: Record<string, unknown>) =>
                  returnReceiptService.getSerialNumbers(receipt.id, params)) as any,
              }}
              searchable={true}
              columnsEditable={false}
              tableId="operatorReturnReceiptSerialNumbersTable"
              defaultQuery={{ sorting_field: 'serial_numbers.created_at', sorting_direction: 'desc' }}
              supportRules={[]}
            />
          ),
        }]}
      />
    </div>
  );
}
